/**
 * hybrid-encode-lane.js — window.HybridEncodeLane
 *
 * The lane for video the browser CANNOT DECODE but CAN ENCODE.
 *
 * WHY IT EXISTS. `webcodecs-pipeline` needs both halves: no decoder for the
 * source codec and the whole export drops to FFmpeg-WASM — decode, filters and
 * encode, all in software, single-threaded on a phone. Measured on prod over 30
 * days: that lane costs 276.7 s per source MB against 25.0 for the hardware one
 * — 12.6×, on 14 % of completions (1484 exports, mean 42 minutes against 3.3).
 * A Pro user spent 3 h 18 min on a 4½-minute clip that way.
 *
 * The gate was all-or-nothing for no reason. In the very session above our own
 * probe printed `enc_verify_probe {1920×1080, ok:1, hw:1}` a second before the
 * refusal: the encoder was there and working. Decoding and encoding are separate
 * concerns; only the decoder was missing.
 *
 * SHAPE, and every part of it is a measurement rather than a preference:
 *
 *     FFmpeg decodes ──▶ WebGL filters ──▶ VideoEncoder ──▶ mp4-muxer
 *     (only it can        (this pipeline's   (hardware)      (+ the original
 *      read the codec)     own rig)                           audio, copied)
 *
 *  · FFMPEG DECODES **AND FILTERS**; only the encoder moves to hardware.
 *    ⚠️ This was nearly built the other way round. A first comparison put
 *    "FFmpeg decodes only" against "FFmpeg decodes and filters" and the first
 *    won by 28–42 % — but in that variant NOBODY filtered, so it measured the
 *    absence of work rather than a cheaper place to do it. With both variants
 *    doing the same work (hybrid-filter-placement.mjs, alternated, medians of
 *    three rounds on a 20 s 1080p clip):
 *        filter in FFmpeg   14.7 s   of which the frame loop  1.2 s
 *        filter on the GPU  43.3 s   of which the frame loop 37.1 s
 *    A texture upload plus a frame readback costs ~69 ms per 1080p frame, and
 *    540 of them dwarf everything the GPU saves. Filtering in FFmpeg is 66 %
 *    faster — and it keeps this cohort's picture IDENTICAL to what the software
 *    lane gives them today, which is the least surprising outcome for them.
 *  · FRAMES ARRIVE IN CHUNKS (FFmpegProcessor.processFrameChunks): peak memory is
 *    one chunk, not the video — 20 s of 1080p raw is 1.6 GB.
 *  · THE FRAME SIZE IS MEASURED, NOT TAKEN ON TRUST. FFmpeg autorotates on
 *    decode, so a phone clip stored 480×854 with a 90° matrix arrives 854×480 —
 *    and the byte count cannot tell the two apart. One PNG frame settles it
 *    before anything else runs.
 *
 * NOT A REPLACEMENT for webcodecs-pipeline: when the browser can decode the
 * source, that lane is faster still (no raw round-trip at all) and owns audio
 * editing, interpolation and stylize. This one is for the files it turns away.
 *
 *   const cap = HybridEncodeLane.isCandidate({
 *       skipReason, outFormat, width, height, fps, durationSec,
 *   });
 *   if (cap.ok) {
 *       const res = await HybridEncodeLane.run({
 *           file, ffmpegFilter, crf, fps, outWidth, outHeight,
 *           outWidth, outHeight, keepAudio: true, onProgress, signal,
 *       });
 *   }
 */
(function (w) {
    'use strict';

    if (w.HybridEncodeLane) return;

    /* Which of the fast lane's refusals this lane can actually answer.
       Counted on prod, 30 days, /enhance-video (1488 skips in total):
         container-or-codec              975   ← no decoder for this source
         upmix                           111       audio work: not ours
         fmt-webm / fmt-mkv              118       we mux mp4
         webgl2-disabled / no-webgl2     122   ← we need WebGL too, so NOT ours
         decoder-rejects-hevc             60   ← no decoder
         no-webcodecs                     51       no encoder either
         encoder-rejects-all-h264-profiles 44      no encoder
       ⚠️ The WebGL ones look like ours — FFmpeg would do the filtering, one
       might think — but this lane filters on the GPU precisely because that is
       what makes it fast. Without WebGL there is nothing to win. */
    var ANSWERABLE = { 'container-or-codec': 1, 'decoder-rejects-hevc': 1 };
    var ANSWERABLE_PREFIX = ['decoder-rejects-'];

    /* Chromium treats `prefer-hardware` as a REQUIREMENT, not a hint: with no
       hardware encoder for the configuration it rejects AND CLOSES the codec,
       and the next encode() throws "Cannot call 'encode' on a closed codec".
       Same ladder and same Android inversion as webcodecs-pipeline's
       ENCODER_HINTS — kept in step with it deliberately, since it is the same
       encoder on the same devices. */
    var ENCODER_HINTS = (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || ''))
        ? ['prefer-software', 'prefer-hardware', null]
        : ['prefer-hardware', 'prefer-software', null];

    /* Seconds of video per decoded chunk. The trade is memory against per-call
       overhead: at 1080p a second of raw frames is ~84 MB, and a process() call
       costs 0.05–0.13 s fixed. Two seconds keeps the peak around 170 MB — fine
       on a 4 GB phone — while the fixed cost stays under 2 % of the run. */
    var CHUNK_SEC = 2;

    function ae(f, p) {
        try {
            if (w.__ae) w.__ae('feature_use', Object.assign({ f: f, lane: 'hybrid' }, p || {}));
        } catch (e) { }
    }

    function isCandidate(o) {
        o = o || {};
        var reason = String(o.skipReason || '');
        if (!w.FFmpegProcessor || typeof w.FFmpegProcessor.processFrameChunks !== 'function') {
            return { ok: false, reason: 'no-frame-source' };
        }
        if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') {
            return { ok: false, reason: 'no-webcodecs' };
        }
        if (typeof OffscreenCanvas === 'undefined') return { ok: false, reason: 'no-offscreen-canvas' };
        // We mux mp4. A webm/mkv request belongs to the software lane.
        if (o.outFormat && o.outFormat !== 'mp4') return { ok: false, reason: 'fmt-' + o.outFormat };
        /* TOO SMALL TO BE WORTH IT. The lane carries a fixed cost — a probe
           frame, the stream-copy split, the muxer, the audio copy — of roughly
           two seconds, and below a certain amount of work the software lane
           simply finishes first. Measured, not guessed: at 176×144×75 frames
           (1.9 Mpx of decoding) the hybrid took 2.1 s against the software
           lane's 0.8 s — 0.36×, a clear loss; at 832×468×150 (58 Mpx) it won
           3.42×. The software lane runs at ~0.32 s per megapixel on this class
           of machine, so the crossover sits near 6 Mpx; 12 leaves a margin. */
        var work = (o.width | 0) * (o.height | 0) * (o.fps || 30) * (o.durationSec || 0);
        if (work > 0 && work < 12e6) return { ok: false, reason: 'too-small' };
        var answerable = !!ANSWERABLE[reason];
        if (!answerable) {
            for (var i = 0; i < ANSWERABLE_PREFIX.length; i++) {
                if (reason.indexOf(ANSWERABLE_PREFIX[i]) === 0) { answerable = true; break; }
            }
        }
        if (!answerable) return { ok: false, reason: 'skip-not-ours:' + (reason || 'unknown') };
        return { ok: true };
    }

    /* ⚠️ The pipeline's loader, not a second one. Mine fetched
       `/vendor/mp4-muxer/mp4-muxer.min.js` by bare path — and nginx on the intl
       shard serves /vendor with `immutable, max-age=15552000`, so anyone who
       already had that file would keep it for 180 days no matter what we ship.
       That is a known class here (window.__jsSrc exists for it), and I had just
       added another instance of it. The shared loader carries the version stamp,
       the CDN fallback and the in-flight promise that stops two callers fetching
       it twice; a copy drifts from all three. */
    /* Mp4CopyTrim joins the picture and the sound on disk (byte ranges). */
    var copyTrimLoading = null;
    function ensureCopyTrim() {
        if (w.Mp4CopyTrim && typeof w.Mp4CopyTrim.concat === 'function') return Promise.resolve(w.Mp4CopyTrim);
        if (copyTrimLoading) return copyTrimLoading;
        copyTrimLoading = new Promise(function (res) {
            var el = w.document.createElement('script');
            var n = w.document.querySelector('script[nonce]');
            if (n && n.nonce) el.setAttribute('nonce', n.nonce);
            el.src = (typeof w.__jsSrc === 'function') ? w.__jsSrc('/js/mp4-copy-trim.js') : '/js/mp4-copy-trim.js';
            el.onload = function () { res(w.Mp4CopyTrim || null); };
            el.onerror = function () { copyTrimLoading = null; res(null); };
            w.document.head.appendChild(el);
        });
        return copyTrimLoading;
    }

    function ensureMuxer() {
        if (w.Mp4Muxer) return Promise.resolve();
        if (w.WebCodecsPipeline && typeof w.WebCodecsPipeline.ensureMp4Muxer === 'function') {
            return w.WebCodecsPipeline.ensureMp4Muxer().then(function () {
                if (!w.Mp4Muxer) throw new Error('mp4-muxer-unavailable');
            });
        }
        return Promise.reject(new Error('mp4-muxer-unavailable'));
    }

    /* CRF → bitrate. The software lane speaks CRF and VideoEncoder speaks bits
       per second, so something has to translate. Anchored on the measured
       output of the lane being replaced: at CRF 18 a 1080p27 clip came out at
       ~10.7 Mbit/s, and the hybrid at 12 Mbit/s produced a file within 12 % of
       it. Scaled by pixel count and frame rate so 720p and 4 K are not given a
       1080p budget. */
    function bitrateFor(crf, width, height, fps) {
        var mp = (width * height) / (1920 * 1080);
        var rate = (fps || 30) / 30;
        var base = 12e6 * Math.pow(0.75, ((crf || 23) - 18) / 3);
        return Math.max(500e3, Math.round(base * mp * rate));
    }

    // The bits the encoder WRITES are bitrate ÷ the told rate per frame, and on
    // Windows bitrate × told must not reach 2³²: the core's rule
    // (window.__encoderRate) rewrites the config's bitrate / framerate.
    function applyEncoderRate(cfg, hint) {
        var f = w.__encoderRate;
        if (typeof f !== 'function' || !(cfg.framerate > 0)) return;
        var r = f(cfg.bitrate, cfg.framerate, cfg.framerate, hint || null);
        cfg.bitrate = r.bitrate; cfg.framerate = r.framerate;
    }

    // `hints` — the cascade to walk; the wedge rebuild passes the rungs AFTER
    // the one that froze, the first pick walks the whole ladder.
    // `sw` — {bps, codec} of the software rung (window.__swEncoderBudget), or null.
    async function configureEncoder(cfgBase, onChunk, onError, hints, sw) {
        var lastErr = null;
        var ladder = (hints && hints.length) ? hints : ENCODER_HINTS;
        for (var i = 0; i < ladder.length; i++) {
            var hint = ladder[i];
            var cfg = Object.assign({}, cfgBase);
            if (hint) cfg.hardwareAcceleration = hint;
            var swOn = !!(sw && hint === 'prefer-software' && sw.bps > cfg.bitrate);
            if (swOn) { cfg.bitrate = sw.bps; cfg.codec = sw.codec; }
            var want = cfg.bitrate;
            applyEncoderRate(cfg, hint);
            var supported = false;
            try {
                var s = await VideoEncoder.isConfigSupported(cfg);
                supported = !!(s && s.supported);
            } catch (e) { supported = false; }
            if (!supported) { lastErr = 'unsupported@' + (hint || 'default'); continue; }
            var failed = null;
            var enc = new VideoEncoder({
                output: onChunk,
                error: function (e) { failed = e; onError(e); },
            });
            try {
                enc.configure(cfg);
                // configure() reports asynchronously: give the error callback a
                // turn before handing it a thousand frames.
                await new Promise(function (r) { setTimeout(r, 0); });
                if (failed) throw failed;
                // 'no-hint' — the pipeline's spelling of the last rung, so the
                // shared cascade helper recognises it on a wedge rebuild.
                return { encoder: enc, hint: hint || 'no-hint', bps: want, gain: swOn ? +(want / cfgBase.bitrate).toFixed(2) : 1 };
            } catch (e) {
                lastErr = (e && e.message) || String(e);
                try { enc.close(); } catch (_) { }
            }
        }
        /* Last resort, UNVERIFIED — the pipeline's doctrine (pickEncoderConfig):
           `isConfigSupported` answering false for every rung is far more often
           a wedged GPU process than a device without H.264 — the same browser
           encodes fine the next day. 91 runs in 30 days surrendered here on
           the probe's word alone ("unsupported@default"), 27 of them desktop
           Yandex on Windows. configure() is the authority: a genuinely broken
           encoder throws within a tick and the caller's software path runs;
           trusting the probe cost the whole lane. */
        if (!(hints && hints.length)) {
            var lrCfg = Object.assign({}, cfgBase);
            var lrHint = ladder[ladder.length - 1];
            if (lrHint) lrCfg.hardwareAcceleration = lrHint;
            var lrWant = lrCfg.bitrate;
            applyEncoderRate(lrCfg, lrHint);
            var lrFailed = null;
            var lrEnc = new VideoEncoder({ output: onChunk, error: function (e) { lrFailed = e; onError(e); } });
            try {
                lrEnc.configure(lrCfg);
                await new Promise(function (r) { setTimeout(r, 0); });
                if (lrFailed) throw lrFailed;
                ae('hybrid_encoder_unverified_attempt', { probe_said: lastErr });
                return { encoder: lrEnc, hint: lrHint || 'no-hint', unverified: true, bps: lrWant, gain: 1 };
            } catch (e) {
                lastErr = 'unverified:' + ((e && e.message) || String(e));
                try { lrEnc.close(); } catch (_) { }
            }
        }
        throw new Error('encoder-failed: ' + lastErr);
    }

    /* What one decoded frame ACTUALLY measures, asked of FFmpeg rather than of
       the caller.
       ⚠️ This is not defensive politeness, it is the only way to be right about
       ROTATION. FFmpeg autorotates on decode: a phone clip stored 480×854 with
       a 90° matrix comes out of the decoder 854×480. A caller that reports the
       STORED size — which is what a container probe gives you — would have this
       lane scale the upright frames back into portrait, squashing every one of
       them. And the byte count cannot catch it: 480×854 and 854×480 are the
       same number of bytes.
       One PNG frame costs ~0.1–0.3 s (a whole process() call with a 50 MB input
       measured 0.05–0.13 s), which is nothing against a run of tens of seconds,
       and it removes the entire class. */
    /* Measured AFTER the caller's own filter chain (2026-10-02). Measured before
       it, the lane took the SOURCE size as the output size and appended
       `scale=<source>` behind the caller's chain — which undid it: a 544×240
       film the person had set to 720p came out 544×240 again. */
    async function probeFrameSize(file, signal, vf) {
        var args = ['-frames:v', '1', '-an'];
        if (vf) args.push('-vf', vf);
        var res = await w.FFmpegProcessor.process(file, args.concat(['-f', 'image2', '-c:v', 'png']), 'hybrid_probe.png');
        /* A probe, not a result: without this the page's blob auto-tracker counted
           the 12 KB frame as the tool's output (tool_complete src:blob, octet-stream). */
        try { if (res && res.blob && w.__skipBlobAutoTrack) w.__skipBlobAutoTrack(res.blob); } catch (e) { }
        try { if (res && res.url) URL.revokeObjectURL(res.url); } catch (e) { }
        if (signal && signal.aborted) { var ab = new Error('aborted'); ab.name = 'AbortError'; throw ab; }
        var bmp = await createImageBitmap(res.blob);
        var dims = { width: bmp.width, height: bmp.height };
        try { bmp.close(); } catch (e) { }
        if (!dims.width || !dims.height) throw new Error('hybrid: could not measure a decoded frame');
        return dims;
    }

    async function run(o) {
        if (!o || !o.file) throw new Error('hybrid: no file');
        var geo = await probeFrameSize(o.file, o.signal, o.ffmpegFilter || '');
        var srcW = geo.width, srcH = geo.height;
        /* The caller may still ask for a different OUTPUT size, but the aspect it
           should scale within is the decoded one, not whatever the container claimed. */
        var outW = (o.outWidth | 0) || srcW, outH = (o.outHeight | 0) || srcH;
        // H.264 wants even dimensions; an odd request silently corrupts chroma.
        outW -= outW % 2; outH -= outH % 2;
        /* Frames are dated n / fps: the rate must be the exact one, not FFmpeg's
           two-decimal print (23.98 drifts 1.2 s over two hours). */
        var fps = (w.FFmpegProcessor && typeof w.FFmpegProcessor.exactFps === 'function' && w.FFmpegProcessor.exactFps(o.fps)) || o.fps || 30;
        var t0 = (w.performance || Date).now();
        var bitrate = o.bitrate || bitrateFor(o.crf, outW, outH, fps);
        /* The software rung's own budget — the one rule of window.__swEncoderBudget
           (timbrica-core.js), shared with the pipeline. This lane is the one Android
           exports reach on AVI/MPG/WMV, and its software encoder starved at the CRF
           table's rate: «как будто видео жестко ужали», 08.10.2026. On by default;
           `softwareEncoderGain: false` opts out; never on an explicit o.bitrate. */
        var PW = w.WebCodecsPipeline || {};
        var swBps = (o.softwareEncoderGain !== false && !o.bitrate && typeof w.__swEncoderBudget === 'function')
            ? w.__swEncoderBudget(bitrate, outW, outH, fps) : 0;
        if (!(swBps > bitrate)) swBps = 0;
        var swFirst = ENCODER_HINTS[0] === 'prefer-software';

        await ensureMuxer();

        /* The picture goes to DISK when it is big (2026-10-02): an ArrayBufferTarget
           held the whole video in the page — a two-hour film at 720p is gigabytes —
           and a JS buffer past 2 GiB does not exist at all. */
        var estBytes = Math.round(((swBps > bitrate && swFirst) ? swBps : bitrate) / 8 * (+o.durationSec || 0) * 1.15);
        var disk = null;
        if (estBytes > 96 * 1048576 && typeof w.__opfsMuxLane === 'function' && w.Mp4Muxer.StreamTarget) {
            try { disk = await w.__opfsMuxLane(w.Mp4Muxer, { prefix: '_hy_', ext: 'mp4', lane: 'hybrid-encode-lane', reserveBytes: estBytes }); }
            catch (e) { disk = null; }
        }
        /* A private window keeps that «disk» in RAM. A 140-min film (632 MB) joined fine
           there on a clean run, and once failed at the join («A requested file … could
           not be found») on a machine with its memory committed — the counts below say
           whether people meet it. */
        var diskInMem = false;
        if (disk && typeof w.__opfsInMemory === 'function') { try { diskInMem = !!(await w.__opfsInMemory()); } catch (e) { } }
        /* The pipeline's rule (openMuxTarget, docs/why/primitives/storage-room.md):
           a lane that could not hold its room, or one on a SHORT disk, is where
           writes are refused minutes in — memory takes the file when it fits
           there, as BLOCKS (one buffer dies at ~1 GiB on finalize). */
        var memCeil = 0;
        try { memCeil = typeof w.__memoryMuxCeiling === 'function' ? (+w.__memoryMuxCeiling() || 0) : 0; } catch (e) { }
        var laneDoubt = disk ? ((!disk.reserved && disk.unreservedWhy) ? String(disk.unreservedWhy) : (disk.diskShort ? 'short' : '')) : '';
        if (laneDoubt && memCeil && estBytes <= memCeil) {
            ae('hybrid_unreserved_to_memory', { why: laneDoubt.slice(0, 40), estMB: Math.round(estBytes / 1048576) });
            try { disk.abort(); } catch (e) { }
            disk = null;
        }
        var mem = (!disk && memCeil && estBytes > 96 * 1048576 && estBytes <= memCeil && typeof w.__memoryMuxTarget === 'function')
            ? w.__memoryMuxTarget(w.Mp4Muxer) : null;
        if (!disk && !mem && estBytes > 1400 * 1048576) {
            ae('hybrid_output_too_big', { estMB: Math.round(estBytes / 1048576) });
            throw new Error('hybrid: the picture (~' + Math.round(estBytes / 1048576) + ' MB) has no room on disk and does not fit memory');
        }

        var tFrames = 0;
        var muxer = new w.Mp4Muxer.Muxer({
            target: disk ? disk.target : (mem ? mem.target : new w.Mp4Muxer.ArrayBufferTarget()),
            video: { codec: 'avc', width: outW, height: outH },
            fastStart: (disk || mem) ? false : 'in-memory',
            /* The muxer's default ('strict') throws when the first chunk's
               decode timestamp is not exactly 0 — and 74 runs in 30 days died
               on that sentence ("The first chunk for your media track must
               have a timestamp of 0"): an encoder that reorders or drops its
               opening frame hands the muxer a first DTS of one frame period.
               'offset' rebases the stream so the first chunk lands at 0, the
               same normalisation webcodecs-pipeline does by hand on its own
               chunks (videoChunkOffset). A shift of one frame period against
               the copied audio is far below what `-shortest` already tolerates. */
            firstTimestampBehavior: 'offset',
        });
        // The wedge recovery below rebuilds the encoder: its parameter sets travel in-band (__muxVideoTrack).
        var muxVideo = w.__muxVideoTrack ? w.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };

        var encoderError = null, encoded = 0;
        /* ⚠️ THE LEVEL IS COMPUTED, NOT CONSTANT. `avc1.640028` is High@4.0 —
           about 8192 macroblocks, i.e. 1920×1080. Measured on the matrix: an
           HEVC source at 2896×1630 (the mean of 381 real runs — iPhone
           footage) made isConfigSupported answer false for EVERY hint, and the
           lane failed outright on the biggest cohort it was built for. The
           pipeline already owns the level table; borrow it rather than keep a
           second copy that drifts. */
        var lvl = (w.WebCodecsPipeline && w.WebCodecsPipeline.h264LevelHex)
            ? w.WebCodecsPipeline.h264LevelHex(outW, outH, fps) : '28';
        var cfg = {
            codec: 'avc1.6400' + lvl, width: outW, height: outH,
            bitrate: bitrate,
            framerate: fps, avc: { format: 'avc' },
            /* No B-frames. This lane feeds chunks straight to the muxer in the
               order the encoder emits them, and mp4-muxer requires monotonic
               DTS — but Firefox's encoder reorders (B-frames) unless asked not
               to, so `addVideoChunk` rejected with "DTS went from 320000 to
               280000" (verified in Playwright Firefox). webcodecs-pipeline sets
               this too; only a HINT, harmless where B-frames are already off. */
            latencyMode: 'realtime',
        };
        // The software rung — its budget and a level that carries it (4.0 tops out at 20 Mbit/s).
        var swRung = (swBps > bitrate) ? {
            bps: swBps,
            codec: 'avc1.6400' + ((PW.h264LevelHex) ? PW.h264LevelHex(outW, outH, fps, swBps) : lvl)
        } : null;
        var encGain = 1, encBps = bitrate;
        /* ── Wedge recovery, the pipeline's doctrine (2026-09-12) ─────────
           This lane was born to rescue runs the fast lane could not decode —
           and it inherited the fast lane's blind spot: a hardware encoder that
           accepts frames and emits nothing raises no error, and the loop below
           used to spin on `encodeQueueSize` forever, past the caller's cancel.
           In the session behind comment #1645 the hardware encoder had JUST
           wedged in the fast lane, and this lane rebuilt the very same
           hardware encoder with 'prefer-hardware' first.

           So the encoder is watched the way webcodecs-pipeline watches its
           own (visible-time silence while frames are owed, ENC_WEDGE_MS), every
           submitted VideoFrame is RETAINED until its chunk lands, and a wedge
           closes the codec, configures the next rung of the hint cascade after
           the wedged one, and replays every unconfirmed frame — lossless, once
           per run. A second wedge is a typed failure that the caller's
           software path answers, exactly as any other lane error. */
        var P = w.WebCodecsPipeline || {};
        var visNow = (typeof P.visibleNow === 'function') ? P.visibleNow : function () { return (w.performance || Date).now(); };
        var WEDGE_MS = P.ENC_WEDGE_MS || 12000;
        var sent = new Map();                 // ts → VideoFrame, unconfirmed
        var owedSinceVis = -1, lastOutVis = -1;
        var wedges = 0, recovered = false, forceKeyNext = false;
        var chosenHint = null;
        /* B-frame-safe muxing — the pipeline's own solution, because this lane
           hit the pipeline's own bug. `latencyMode:'realtime'` asks for no
           B-frames and is only a HINT: verified in Playwright Firefox, the
           encoder reorders anyway and the muxer rejected the run with
           "Timestamps must be monotonically increasing (DTS went from 320000 to
           280000)". Chunks always arrive in DECODE order, so DTS is the running
           sum of their durations (strictly monotonic) and the muxer gets
           compositionTimeOffset = CTS − DTS. Without B-frames DTS === CTS and
           the offset is 0 — a no-op. */
        var dtsAccumUs = 0;
        var frameDurFallbackUs = 1e6 / (fps || 30);
        var sanitize = (typeof P.sanitizeChunkMeta === 'function') ? P.sanitizeChunkMeta : function (m) { return m; };
        /* Which frame a chunk is (w.__encoderClock, timbrica-core.js): the same
           Firefox encoder stamps a chunk with the END of its frame — the picture
           went out one frame late and `sent` released the NEXT frame's copy, so a
           wedge rebuild would replay the wrong frames. */
        var encClock = w.__encoderClock ? w.__encoderClock({ onEnd: function () { ae('hybrid_chunk_end_stamped', { hint: chosenHint || 'default' }); } }) : null;
        function onChunk(chunk, meta) {
            var ts = encClock ? encClock.ts(chunk) : chunk.timestamp;
            var f = sent.get(ts);
            if (f) { try { f.close(); } catch (e) { } sent.delete(ts); }
            lastOutVis = visNow();
            encoded++;
            var dts = dtsAccumUs;
            dtsAccumUs += (chunk.duration > 0 ? chunk.duration : frameDurFallbackUs);
            try { muxVideo(chunk, sanitize(meta), ts, ts - dts); }
            catch (e) { encoderError = e; }
        }
        function onError(e) { if (!encoderError) encoderError = e; }
        function silentMs() {
            if (!sent.size) return 0;
            var base = Math.max(owedSinceVis, lastOutVis);
            return base < 0 ? 0 : visNow() - base;
        }
        function wedgeCheck(phase) {
            if (encoderError) return false;
            var s = silentMs();
            if (s < WEDGE_MS) return false;
            wedges++;
            var e = new Error('hybrid: encoder wedged: no output for ' + Math.round(s / 1000) + 's with ' + sent.size + ' frame(s) owed (' + phase + ')');
            e.name = 'EncoderWedged';
            encoderError = e;
            ae('hybrid_encoder_wedge', { phase: phase, owed: sent.size, silent_ms: Math.round(s),
                hint: chosenHint || 'default', encoded: encoded, recovered_before: recovered ? 1 : 0 });
            return true;
        }
        function submit(frame, key) {
            if (!sent.size) owedSinceVis = visNow();
            sent.set(frame.timestamp, frame);
            if (encClock) encClock.fed(frame.timestamp, frame.duration || 0);
            enc.encode(frame, { keyFrame: !!(key || forceKeyNext) });
            forceKeyNext = false;
        }
        // Rebuild on the next hint and replay what the dead instance swallowed.
        // Returns false when there is no rung left or the new codec refuses.
        async function rebuildAfterWedge() {
            if (recovered) return false;
            recovered = true;
            try { enc.close(); } catch (e) { }
            var next = (typeof P.encoderHintsAfter === 'function') ? P.encoderHintsAfter(chosenHint) : [];
            if (!next.length) {
                ae('hybrid_encoder_wedge_unrecoverable', { hint: chosenHint || 'default', reason: 'no-hint-left' });
                return false;
            }
            var setup;
            try { setup = await configureEncoder(cfg, onChunk, onError, next, swRung); }
            catch (e) {
                ae('hybrid_encoder_wedge_unrecoverable', { hint: chosenHint || 'default', reason: String((e && e.message) || e).slice(0, 60) });
                return false;
            }
            enc = setup.encoder; chosenHint = setup.hint;
            if (encClock) encClock.restart();   // the new instance states its own clock
            encGain = setup.gain || 1; encBps = setup.bps || bitrate;
            encoderError = null;
            owedSinceVis = -1; lastOutVis = -1;
            var items = Array.from(sent.entries()).sort(function (a, b) { return a[0] - b[0]; });
            sent.clear();
            forceKeyNext = true;
            for (var i = 0; i < items.length; i++) submit(items[i][1], i === 0);
            ae('hybrid_encoder_wedge_recovered', { replayed: items.length, hint: chosenHint });
            return true;
        }
        // Wait until the encoder has room (or is found wedged / cancelled).
        async function waitForRoom() {
            if (enc.encodeQueueSize <= 24) return;
            while (enc.encodeQueueSize > 8) {
                if (o.signal && o.signal.aborted) { var ab = new Error('aborted'); ab.name = 'AbortError'; throw ab; }
                if (encoderError) return;
                if (wedgeCheck('encode')) return;
                await new Promise(function (r) { setTimeout(r, 8); });
            }
        }
        async function flushBounded() {
            var settled = false;
            try { enc.flush().then(function () { settled = true; }, function () { settled = true; }); }
            catch (e) { settled = true; }
            var idle = visNow();
            while (!settled) {
                if (o.signal && o.signal.aborted) return;
                if (encoderError) return;
                if (sent.size) { if (wedgeCheck('flush')) return; idle = visNow(); }
                else if (visNow() - idle > WEDGE_MS) return;      // owes nothing: leave it
                await new Promise(function (r) { setTimeout(r, 25); });
            }
        }
        function releaseAll() { sent.forEach(function (f) { try { f.close(); } catch (e) { } }); sent.clear(); }

        var encSetup = await configureEncoder(cfg, onChunk, onError, null, swRung);
        var enc = encSetup.encoder;
        chosenHint = encSetup.hint;
        encGain = encSetup.gain || 1; encBps = encSetup.bps || bitrate;

        var frameBytes = outW * outH * 1.5;
        var frames = 0;
        var gopFrames = Math.max(1, Math.round(fps * 2));

        /* Frames come in EXACT windows (FFmpegProcessor.decodeWindowsStreaming) when
           the engine has them: the stream-copy split below does not cut an AVI with
           packed B-frames at all (one raw buffer for the whole film), cuts it at a
           6-byte N-VOP into grey frames with +genpts, and drops ~2 frames at every
           cut of a DVD VOB. The split stays only for an engine without the door. */
        var vfr = false;
        var frameSource = (typeof w.FFmpegProcessor.decodeWindowsStreaming === 'function')
            ? function (fo) {
                return w.FFmpegProcessor.decodeWindowsStreaming(o.file, {
                    vf: fo.videoFilter, frameBytes: frameBytes, pixFmt: 'yuv420p',
                    durationS: +o.durationSec || 0, fps: fps, rawBudgetBytes: o.rawBudgetBytes,
                    /* The door decides the rate its frames come at — the FILE's (a variable-
                       rate clip at its average) — and frame n is dated n / THAT rate. Dating
                       at the caller's number while FFmpeg emitted its own guess made 40 s of
                       fast motion out of a 60 s variable-rate clip (2026-10-02). */
                    onClock: function (c) {
                        if (!c || !(c.fps > 0)) return;
                        fps = c.fps; vfr = !!c.vfr;
                        gopFrames = Math.max(1, Math.round(fps * 2));
                        frameDurFallbackUs = 1e6 / fps;
                    },
                    signal: fo.signal, onChunk: fo.onChunk, onProgress: fo.onProgress
                });
            }
            : function (fo) { return w.FFmpegProcessor.processFrameChunks(o.file, fo); };
        try {
            await frameSource({
                chunkSec: o.chunkSec || CHUNK_SEC,
                pixFmt: 'yuv420p',
                // The ONLY filter FFmpeg runs. Explicit scale settles two things
                // at once: every raw frame is exactly outW×outH so the byte
                // stream can be cut into frames without guessing, and rotation
                // metadata (which rawvideo cannot carry) is baked in by the
                // autorotate that precedes this filter.
                /* The tool's own filter chain, then an explicit scale. The
                   scale is not optional even at 1:1: it fixes the raw frame
                   size so the byte stream can be cut into frames without
                   guessing, and it bakes in the rotation that `rotate=90`
                   metadata carries — rawvideo cannot, and a portrait clip
                   decoded as landscape is a silent, total corruption. */
                videoFilter: (o.ffmpegFilter ? o.ffmpegFilter + ',' : '') + 'scale=' + outW + ':' + outH,
                signal: o.signal,
                /* The picture is 85 % of the bar, the one sound pass the next 13 %: on a
                   long film that pass alone runs for minutes (27 min for 140 min). */
                onProgress: function (p) { if (o.onProgress) { try { o.onProgress(p * 0.85); } catch (e) { } } },
                onChunk: async function (bytes) {
                    var _tc = (w.performance || Date).now();
                    if (bytes.length % frameBytes !== 0) {
                        throw new Error('hybrid: chunk of ' + bytes.length + ' B is not whole frames of ' + frameBytes);
                    }
                    var n = bytes.length / frameBytes;
                    for (var i = 0; i < n; i++) {
                        if (o.signal && o.signal.aborted) { var ab = new Error('aborted'); ab.name = 'AbortError'; throw ab; }
                        // Backpressure BEFORE the frame: hardware encoders
                        // pipeline several frames deep, so a queue is healthy —
                        // an unbounded one is not, and one that never drains
                        // is a wedge (waitForRoom declares it).
                        await waitForRoom();
                        wedgeCheck('encode');
                        if (encoderError) {
                            if (encoderError.name === 'EncoderWedged' && await rebuildAfterWedge()) { /* replayed; carry on */ }
                            else throw encoderError;
                        }
                        /* A refused write ends the run HERE, not at finish() an hour
                           later (the muxer never awaits its writes): report #9939
                           waited 1002 s for a refusal seen at 256 MB. */
                        if (disk && disk.stats && disk.stats.failed) {
                            var c = disk.stats.failed;
                            var de = new Error('opfs-write-failed:' + ((c && c.message) || 'refused'));
                            de.diskFull = !!(c && (c.name === 'QuotaExceededError' || c.code === 22)) || /quota/i.test(String(c && c.message));
                            de.needBytes = estBytes;
                            throw de;
                        }
                        var view = new Uint8Array(bytes.buffer, bytes.byteOffset + i * frameBytes, frameBytes);
                        // The VideoFrame COPIES the bytes, so it can outlive the
                        // chunk buffer: it is closed when its chunk lands (onChunk)
                        // or replayed into a rebuilt encoder — never here.
                        var src = new VideoFrame(view, {
                            format: 'I420', codedWidth: outW, codedHeight: outH,
                            timestamp: Math.round((frames / fps) * 1e6),
                            duration: Math.round((1 / fps) * 1e6),
                        });
                        submit(src, frames % gopFrames === 0);
                        frames++;
                    }
                    tFrames += (w.performance || Date).now() - _tc;
                },
            });
            await flushBounded();
            if (encoderError && encoderError.name === 'EncoderWedged' && await rebuildAfterWedge()) {
                await flushBounded();
            }
        } catch (e) {
            if (disk) disk.abort();
            if (mem) { try { mem.release(); } catch (_) { } }
            throw e;
        } finally {
            try { enc.close(); } catch (e) { }
            releaseAll();
        }
        if (encoderError) { if (disk) disk.abort(); throw encoderError; }
        if (!frames) { if (disk) disk.abort(); throw new Error('hybrid: no frames decoded'); }
        // Retention makes the rebuild lossless; a shortfall after it is a bug,
        // and the honest answer is to refuse the artifact (the software path
        // below produces the exact file), not to ship a video with a hole.
        if (recovered && encoded < frames) {
            if (disk) disk.abort();
            throw new Error('hybrid: recovered stream is missing ' + (frames - encoded) + ' frame(s)');
        }

        muxer.finalize();
        var videoOnly = disk ? await disk.finish() : (mem ? mem.finish('video/mp4') : new Blob([muxer.target.buffer], { type: 'video/mp4' }));

        /* The sound: ONE pass over the whole file, its zero on the first picture,
           joined to the picture on disk (Mp4CopyTrim.concat, byte ranges, moov
           first). The copy below put the picture AND the source into the wasm heap
           — the same ceiling the disk target above removes. It stays only for an
           engine without the door. A video export that arrives mute is not a
           faster export; a source without sound is passed through. */
        var out = videoOnly, joined = null;
        if (o.keepAudio !== false && typeof w.FFmpegProcessor.soundForPictureStreaming === 'function') {
            try {
                var MCT = await ensureCopyTrim();
                var snd = await w.FFmpegProcessor.soundForPictureStreaming(o.file, { audioArgs: o.audioArgs, fps: fps,
                    durationS: frames / fps,
                    onProgress: function (f) { if (o.onProgress) { try { o.onProgress(0.85 + f * 0.13); } catch (e) { } } } });
                if (!snd) joined = videoOnly;
                else if (MCT) joined = await MCT.concat({ clips: [{ video: new File([videoOnly], 'v.mp4', { type: 'video/mp4' }), audio: snd.audio }],
                    silence: snd.silence, brand: 'mp4', name: 'hybrid_out.mp4' });
            } catch (e) {
                ae('hybrid_audio_join_failed', { err: String((e && e.message) || e).slice(0, 80), disk: disk ? 1 : 0, mem: diskInMem ? 1 : 0,
                    outMB: Math.round(((videoOnly && videoOnly.size) || 0) / 1048576), min: Math.round(frames / fps / 60) });
                /* The picture is on disk and the in-heap copy below is no option:
                   a mute file is not an answer — the caller's software lane is. */
                if (disk) throw new Error('hybrid: the sound could not be joined: ' + String((e && e.message) || e).slice(0, 120));
            }
            if (joined) out = joined;
        }
        if (!joined && o.keepAudio !== false && !disk) {
            try {
                var muxed = await w.FFmpegProcessor.processMulti(
                    [new File([videoOnly], 'v.mp4', { type: 'video/mp4' }), o.file],
                    ['-map', '0:v:0', '-map', '1:a:0?', '-c', 'copy', '-shortest'],
                    'hybrid_out.mp4');
                if (muxed && muxed.blob && muxed.blob.size > videoOnly.size * 0.9) out = muxed.blob;
            } catch (e) {
                // A missing or unusable audio track must not cost the picture.
                ae('hybrid_audio_mux_failed', { err: String((e && e.message) || e).slice(0, 80) });
            }
        }
        /* Теги источника. Их здесь теряют ДВАЖДЫ: mp4-muxer их не пишет, а
           копирующий мукс выше берёт метаданные из ВХОДА 0, то есть из нашего
           же файла без тегов. Общая дверь — одна на все лейны (см. mp4-tags.js
           и обращение #8219). */
        try {
            var _T = w.__mp4Tags;
            if (!_T) {
                var _src = (typeof w.__jsSrc === 'function') ? w.__jsSrc('/js/mp4-tags.js') : '/js/mp4-tags.js';
                await new Promise(function (res) {
                    var s = w.document.createElement('script');
                    s.src = _src; s.onload = res; s.onerror = res;
                    w.document.head.appendChild(s);
                });
                _T = w.__mp4Tags;
            }
            if (_T) out = await _T.carry(o.file, out, { slug: 'hybrid-encode-lane' });
        } catch (_e) { /* fail-open: теги важны, выданный файл важнее */ }

        if (o.onProgress) { try { o.onProgress(1); } catch (e) { } }

        var secs = (((w.performance || Date).now()) - t0) / 1000;
        ae('hybrid_export', {
            frames: frames, encoded: encoded, sec: Math.round(secs),
            outW: outW, outH: outH, fps: fps, vfr: vfr ? 1 : 0,
            srcMB: Math.round((o.file.size || 0) / 1048576),
            outMB: Math.round(out.size / 1048576),
            audio: out !== videoOnly ? 1 : 0, disk: disk ? 1 : 0, mem: diskInMem ? 1 : 0, min: Math.round(frames / fps / 60),
            hint: chosenHint || 'no-hint', wedges: wedges,
            kbps: Math.round(encBps / 1000), gain: encGain,
        });
        return {
            blob: out, frames: frames, encode_sec: secs,
            width: outW, height: outH, lane: 'hybrid',
            encHint: chosenHint || 'no-hint', encBps: encBps, encGain: encGain,
            // Where the time went, so a slow run can be diagnosed instead of
            // guessed at: decode is everything outside onChunk, frame_sec is
            // the filter+encode loop, audio_sec is the copy mux.
            frame_sec: tFrames / 1000,
        };
    }

    w.HybridEncodeLane = {
        isCandidate: isCandidate,
        run: run,
        _bitrateFor: bitrateFor,
        _configureEncoder: configureEncoder,   // tests: the software rung's budget
        __version: 1,
    };
})(window);
